import React, { FC } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";

import { BlockchainExplorerLink } from "./BlockchainExplorerLink";
import { InvoiceSummaryBox } from "./InvoiceSummaryBox";
import {
  DEFAULT_CRYPTO_CURRENCY,
  DEFAULT_FIAT_CURRENCY,
  CURRENCY_MAP,
} from "../../../constants";
import { selectInvoice } from "../../../store/reducers/invoice";
import { InvoiceStatus, TransactionDetails } from "../../../types/InvoiceTypes";
import { formatDate, DateFormat } from "../../../utils/date";
import { formatAmount } from "../../../utils/numbers/money";

type Props = {
  className?: string;
};

const approxLabel = <span className="font-extrabold">approx.</span>;

const getToPayLabel = (status: InvoiceStatus, approx?: boolean) => {
  if ([InvoiceStatus.PROCESSING, InvoiceStatus.PAID].includes(status)) {
    return "Paid";
  }

  if (
    !approx ||
    [InvoiceStatus.PENDING, InvoiceStatus.PENDING_REVIEW].includes(status)
  ) {
    return "To pay";
  }

  return <>To pay {approxLabel}</>;
};

const getToPayHint = (
  status: InvoiceStatus,
  approx?: boolean,
  transaction_details?: TransactionDetails
) => {
  if (status === InvoiceStatus.PAID) {
    if (!transaction_details) {
      return undefined;
    }

    const paidAt = formatDate(
      transaction_details.paid_at,
      DateFormat.d_MMM_yyyy
    );

    return `Date ${paidAt}`;
  }

  return ![
    InvoiceStatus.PENDING,
    InvoiceStatus.PROCESSING,
    InvoiceStatus.PENDING_REVIEW,
  ].includes(status) && approx
    ? "The final amount depends on the exchange rate at the time you issue the invoice"
    : undefined;
};

const getWillConvertLabel = (status: InvoiceStatus) =>
  status !== InvoiceStatus.PAID ? "Will be converted" : "We converted";

const getYouGetLabel = (status: InvoiceStatus, approx?: boolean) => {
  if (status === InvoiceStatus.PAID) {
    return "Got";
  }

  return approx ? <>Get {approxLabel}</> : "Get";
};

const getExchangeRateLabel = ({
  price,
  outgoing_currency,
}: TransactionDetails) =>
  `Exchange rate ${formatAmount(price, 4)} ${CURRENCY_MAP[outgoing_currency].symbol}`;

const getYouGetHint = (
  status: InvoiceStatus,
  approx?: boolean,
  transaction_details?: TransactionDetails
) => {
  if (status === InvoiceStatus.PAID) {
    if (!transaction_details) {
      return undefined;
    }

    return (
      <BlockchainExplorerLink
        label={getExchangeRateLabel(transaction_details)}
        link={transaction_details?.explorer_link}
      />
    );
  }

  return approx && transaction_details
    ? `${getExchangeRateLabel(transaction_details)}. The final amount depends on the exchange rate at the time we receive the payment`
    : approx
      ? "The final amount depends on the exchange rate at the time you issue the invoice"
      : transaction_details
        ? getExchangeRateLabel(transaction_details)
        : undefined;
};

export const InvoiceSummary: FC<Props> = ({ className }) => {
  const invoice = useSelector(selectInvoice);

  if (!invoice) {
    return <></>;
  }

  const {
    transaction_details,
    currency,
    status,
    items_amounts_are_in_outgoing_currency,
  } = invoice;

  const toBeConverted =
    (transaction_details?.amount || 0) -
    (transaction_details?.commission_amount || 0);

  return (
    <div
      className={classNames(
        "flex flex-col md:flex-row justify-between gap-3 md:gap-0 border py-8 border-0 border-t border-violet-50",
        className
      )}
    >
      <InvoiceSummaryBox
        label={getToPayLabel(status, items_amounts_are_in_outgoing_currency)}
        amount={transaction_details?.amount || 0}
        currency={currency || DEFAULT_FIAT_CURRENCY}
        className="basis-1/4"
        hint={getToPayHint(
          status,
          items_amounts_are_in_outgoing_currency,
          transaction_details
        )}
      />

      <InvoiceSummaryBox
        label={`Archway ${transaction_details?.commission_percent}% fee`}
        amount={transaction_details?.commission_amount || 0}
        currency={currency || DEFAULT_FIAT_CURRENCY}
        className="basis-1/4"
      />

      <InvoiceSummaryBox
        label={getWillConvertLabel(status)}
        amount={toBeConverted}
        currency={currency || DEFAULT_FIAT_CURRENCY}
        className="basis-1/4"
      />

      <InvoiceSummaryBox
        label={getYouGetLabel(status, !items_amounts_are_in_outgoing_currency)}
        amount={transaction_details?.outgoing_amount || 0}
        currency={
          transaction_details?.outgoing_currency || DEFAULT_CRYPTO_CURRENCY
        }
        hint={getYouGetHint(
          status,
          !items_amounts_are_in_outgoing_currency,
          transaction_details
        )}
        className="basis-1/4"
      />
    </div>
  );
};
